/**
 * RunSummaryScene - End-of-run screen for both victory and defeat
 * Shows run statistics and offers a fresh start.
 */

import Phaser from 'phaser';
import { GameStateManager } from '../game/GameStateManager';
import { SCENES, GAME_CONFIG } from '../constants/GameConstants';
import { AudioManager } from '../game/AudioManager';
import { T, INK, PALETTE, FONT, makeButton, makePanel, paintBackground, fadeInScene } from '../constants/Theme';

export class RunSummaryScene extends Phaser.Scene {
  private gameStateManager!: GameStateManager;
  private victory = false;

  constructor() {
    super({ key: SCENES.RUN_SUMMARY });
  }

  init(data: { victory?: boolean } = {}): void {
    this.gameStateManager = GameStateManager.getInstance();
    this.victory = !!data.victory;
  }

  create(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const cx = WIDTH / 2;

    const gameState = this.gameStateManager.getGameState();
    const run = gameState.currentRun;

    // End the run BEFORE reading lifetime stats so this run is included.
    this.gameStateManager.endRun(this.victory);

    paintBackground(this, WIDTH, HEIGHT);
    fadeInScene(this);
    AudioManager.getInstance().init(this);

    const accent = this.victory ? PALETTE.gold : PALETTE.red;
    const accentInk = this.victory ? INK.gold : INK.red;

    // Banner
    const crest = this.add.text(cx, 150, this.victory ? '♚' : '☗', {
      fontFamily: FONT, fontSize: '96px', color: accentInk,
    });
    crest.setOrigin(0.5);
    crest.setShadow(0, 5, '#0a0f1c', 12, false, true);

    const title = this.add.text(cx, 260, this.victory ? 'VICTORY' : 'RUN OVER', T.display({ color: accentInk }));
    title.setOrigin(0.5);
    title.setLetterSpacing(3);
    title.setShadow(0, 4, '#0a0f1c', 10, false, true);

    const subtitle = this.add.text(
      cx, 330,
      this.victory ? 'You conquered all 12 floors!' : 'Your army has fallen. The tower awaits.',
      T.body({ align: 'center', color: INK.secondary, wordWrap: { width: WIDTH - 120 } })
    );
    subtitle.setOrigin(0.5);

    // Run stats card
    const floorsReached = run?.currentFloor || 1;
    const durationMs = run ? Date.now() - run.startTime : 0;
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);

    const statLines: Array<[string, string]> = [
      ['Floor reached', `${floorsReached} / ${run?.totalFloors || 12}`],
      ['Battles won', `${run?.battlesWon || 0}`],
      ['Gold earned', `${run?.goldEarned || 0}`],
      ['Upgrades collected', `${(gameState.activeUpgrades || []).length}`],
      ['Army size', `${gameState.playerPieces.length}`],
      ['Run time', `${minutes}m ${seconds}s`],
    ];

    const rowH = 54;
    const cardH = statLines.length * rowH + 36;
    const cardTop = 400;
    const cardCx = cardTop + cardH / 2;
    makePanel(this, cx, cardCx, 600, cardH, { fill: PALETTE.panel, stroke: accent, strokeWidth: 2, radius: 22 });

    statLines.forEach(([label, value], i) => {
      const y = cardTop + 30 + i * rowH;
      if (i > 0) {
        const divider = this.add.graphics();
        divider.lineStyle(1, PALETTE.stroke, 0.6);
        divider.lineBetween(cx - 260, y - rowH / 2, cx + 260, y - rowH / 2);
      }
      const labelText = this.add.text(cx - 262, y, label, T.body({ color: INK.secondary }));
      labelText.setOrigin(0, 0.5);
      const valueText = this.add.text(cx + 262, y, value, T.bodyStrong({ color: INK.primary }));
      valueText.setOrigin(1, 0.5);
    });

    // Lifetime stats line
    const stats = this.gameStateManager.getLifetimeStats();
    const lifetime = this.add.text(
      cx, cardTop + cardH + 28,
      `Lifetime: ${stats.runsWon} wins / ${stats.runsPlayed} runs  ·  Best floor ${stats.bestFloor}`,
      T.small({ color: INK.faint })
    );
    lifetime.setOrigin(0.5);

    // Buttons
    makeButton(this, cx, HEIGHT - 250, 'NEW RUN', {
      fill: PALETTE.green, onClick: () => {
        this.gameStateManager.startNewRun();
        this.scene.start(SCENES.MAP);
      },
    });
    makeButton(this, cx, HEIGHT - 150, 'MAIN MENU', {
      fill: PALETTE.panelRaised, onClick: () => {
        this.scene.start(SCENES.MENU);
      },
    });
  }
}
